import { z } from 'zod';

const fraction = z.number().min(0).max(1);

/** A rectangle on a page: fractions of the page, origin top-left, four decimal places. */
export const rectSchema = z.object({
  page: z.number().int().positive(),
  x: fraction,
  y: fraction,
  w: fraction,
  h: fraction,
});
export type Rect = z.infer<typeof rectSchema>;

/** `[x, y, w, h]` as fractions of the page. */
export const bboxSchema = z.tuple([fraction, fraction, fraction, fraction]);
export type Bbox = z.infer<typeof bboxSchema>;

/** A word of a layout line: its character span `[s, e)` in the line's plain text and its box. */
export const layoutWordSchema = z
  .object({ s: z.number().int().nonnegative(), e: z.number().int().positive(), bbox: bboxSchema })
  .refine((w) => w.e > w.s, { message: 'a word spans at least one character' });
export type LayoutWord = z.infer<typeof layoutWordSchema>;

/**
 * A line of the script as laid out on the PDF. `n` is its 1-based line number in `script.txt` (page-marker lines
 * take a number too, so `n` has gaps at page breaks); `text` is the plain text, tags stripped, that the words'
 * offsets index.
 */
export const layoutLineSchema = z.object({
  n: z.number().int().positive(),
  page: z.number().int().positive(),
  bbox: bboxSchema,
  text: z.string(),
  words: z.array(layoutWordSchema),
});
export type LayoutLine = z.infer<typeof layoutLineSchema>;

export const layoutPageSchema = z.object({
  number: z.number().int().positive(),
  width: z.number().positive(),
  height: z.number().positive(),
});
export type LayoutPage = z.infer<typeof layoutPageSchema>;

export const LAYOUT_FORMAT = 1;

/** `layout.json`: the pages (in points) and every line of the script with its words and boxes, in reading order. */
export const layoutSchema = z.object({
  format: z.literal(LAYOUT_FORMAT),
  pages: z.array(layoutPageSchema).min(1),
  lines: z.array(layoutLineSchema),
});
export type Layout = z.infer<typeof layoutSchema>;

/** The page-marker line that `script.txt` puts before each page. */
export function pageMarker(page: number): string {
  return `==== page ${page} ====`;
}
